import { createId } from "@paralleldrive/cuid2";
import mixpanel from "mixpanel-browser";

import {
  ANALYTICS_MAX_PENDING_EVENTS,
  E_MixpanelDeliveryMode,
  buildMixpanelTrackPayload,
  cleanAnalyticsProperties,
  getMixpanelTrackUrl,
  normalizeMixpanelApiUrl,
  parseMixpanelDeliveryMode,
  resolveMixpanelToken,
  type T_AnalyticsProperties,
  type T_MixpanelDeliveryMode,
} from "@lattice/shared-frontend/analytics";

import { MixpanelEvents } from "./events";

const ANONYMOUS_ID_STORAGE_KEY = "lattice.analytics.anonymous_id";

let isInitialized = false;
let identifiedUserId: string | null = null;
let anonymousDistinctId = createId();
let mixpanelToken: string | null = null;
let mixpanelApiUrl = normalizeMixpanelApiUrl();
let mixpanelDeliveryMode: T_MixpanelDeliveryMode =
  E_MixpanelDeliveryMode.JavaScript;
let baseSuperProperties: T_AnalyticsProperties = {
  platform: "web",
  environment: "local",
};
let superProperties: T_AnalyticsProperties = {};
let userProperties: T_AnalyticsProperties = {};

type T_PendingEvent = {
  name: string;
  properties?: T_AnalyticsProperties;
};

type T_MixpanelInitOptions = {
  apiUrl?: string;
  mode?: string;
};

const pendingEvents: T_PendingEvent[] = [];
const isDev = process.env.NODE_ENV === "development";

function logAnalyticsDebug(message: string, error?: unknown): void {
  if (!isDev) return;

  if (error === undefined) {
    console.debug(message);
    return;
  }

  console.debug(message, error);
}

function isDirectHttpMode(): boolean {
  return mixpanelDeliveryMode === E_MixpanelDeliveryMode.DirectHttp;
}

function getBaseSuperProperties(): T_AnalyticsProperties {
  return {
    platform: "web",
    environment: process.env.NEXT_PUBLIC_APP_ENVIRONMENT ?? "local",
  };
}

function loadAnonymousDistinctId(): string {
  if (typeof window === "undefined") return anonymousDistinctId;

  try {
    const existing = window.localStorage.getItem(ANONYMOUS_ID_STORAGE_KEY);
    if (existing) return existing;

    const nextId = createId();
    window.localStorage.setItem(ANONYMOUS_ID_STORAGE_KEY, nextId);
    return nextId;
  } catch (error: unknown) {
    logAnalyticsDebug(
      "[Mixpanel] Failed to load the persisted anonymous ID",
      error,
    );
    return anonymousDistinctId;
  }
}

function rotateAnonymousDistinctId(): void {
  const nextId = createId();
  anonymousDistinctId = nextId;

  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(ANONYMOUS_ID_STORAGE_KEY, nextId);
  } catch (error: unknown) {
    logAnalyticsDebug(
      "[Mixpanel] Failed to rotate the persisted anonymous ID",
      error,
    );
  }
}

function canQueueBrowserEvent(): boolean {
  return typeof window !== "undefined";
}

function enqueueEvent(name: string, properties?: T_AnalyticsProperties): void {
  if (!canQueueBrowserEvent()) return;

  pendingEvents.push({ name, properties });
  if (pendingEvents.length > ANALYTICS_MAX_PENDING_EVENTS) {
    pendingEvents.shift();
  }
}

function applyIdentityToSdk(userId: string): void {
  mixpanel.identify(userId);
  mixpanel.people.set({ $name: userId });
  mixpanel.register({ user_id: userId });
}

function applyPropertiesToSdk(): void {
  const cleanedSuperProperties = cleanAnalyticsProperties(superProperties);
  if (Object.keys(cleanedSuperProperties).length > 0) {
    mixpanel.register(cleanedSuperProperties);
  }

  const cleanedUserProperties = cleanAnalyticsProperties(userProperties);
  if (Object.keys(cleanedUserProperties).length > 0) {
    mixpanel.people.set(cleanedUserProperties);
  }
}

async function sendDirectMixpanelEvent(
  name: string,
  properties?: T_AnalyticsProperties,
): Promise<void> {
  if (!mixpanelToken) {
    logAnalyticsDebug("[Mixpanel] Token not configured, event skipped");
    return;
  }

  const payload = buildMixpanelTrackPayload({
    anonymousId: anonymousDistinctId,
    clientTransport: "web_direct_http",
    eventName: name,
    eventProperties: properties,
    insertId: createId(),
    superProperties,
    token: mixpanelToken,
    userId: identifiedUserId,
    userProperties,
  });

  try {
    const response = await fetch(getMixpanelTrackUrl(mixpanelApiUrl), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      logAnalyticsDebug(
        `[Mixpanel] Event rejected with HTTP ${response.status}`,
      );
    }
  } catch (error: unknown) {
    logAnalyticsDebug("[Mixpanel] Failed to send event", error);
  }
}

function deliverEvent(name: string, properties?: T_AnalyticsProperties): void {
  if (isDirectHttpMode()) {
    void sendDirectMixpanelEvent(name, properties);
    return;
  }

  mixpanel.track(name, cleanAnalyticsProperties(properties));
}

function flushPendingState(): void {
  if (!isDirectHttpMode()) {
    if (identifiedUserId) {
      applyIdentityToSdk(identifiedUserId);
    }
    applyPropertiesToSdk();
  }

  const events = pendingEvents.splice(0);
  for (const event of events) {
    deliverEvent(event.name, event.properties);
  }
}

/**
 * Initialize Mixpanel. The browser SDK remains the default production
 * transport; direct_http sends the same event contract to a local collector.
 */
export function initMixpanel(
  token?: string,
  initialSuperProperties?: T_AnalyticsProperties,
  options?: T_MixpanelInitOptions,
): boolean {
  if (isInitialized || typeof window === "undefined") return false;

  const configuredMode = parseMixpanelDeliveryMode(options?.mode);
  if (options?.mode?.trim() && !configuredMode) {
    logAnalyticsDebug(
      `[Mixpanel] Unknown MIXPANEL_MODE "${options.mode}", using browser SDK`,
    );
  }
  mixpanelDeliveryMode =
    configuredMode === E_MixpanelDeliveryMode.DirectHttp
      ? E_MixpanelDeliveryMode.DirectHttp
      : E_MixpanelDeliveryMode.JavaScript;
  mixpanelToken = resolveMixpanelToken(token, mixpanelDeliveryMode);
  if (!mixpanelToken) return false;

  mixpanelApiUrl = normalizeMixpanelApiUrl(options?.apiUrl);
  anonymousDistinctId = loadAnonymousDistinctId();
  baseSuperProperties = {
    ...getBaseSuperProperties(),
    ...initialSuperProperties,
    mixpanel_delivery_mode: mixpanelDeliveryMode,
  };
  superProperties = {
    ...baseSuperProperties,
    ...superProperties,
  };

  if (!isDirectHttpMode()) {
    mixpanel.init(mixpanelToken, {
      track_pageview: false,
      persistence: "localStorage",
    });
  }

  isInitialized = true;
  flushPendingState();

  logAnalyticsDebug(`[Mixpanel] Initialized in ${mixpanelDeliveryMode} mode`);
  return true;
}

/** Links future events to the authenticated user. */
export function identifyUser(userId: string): void {
  identifiedUserId = userId;
  superProperties = {
    ...superProperties,
    user_id: userId,
  };

  if (isInitialized && !isDirectHttpMode()) {
    applyIdentityToSdk(userId);
  }

  logAnalyticsDebug("[Mixpanel] Identified user", userId);
}

/** Clears user identity and rotates the anonymous identifier. */
export function resetUser(): void {
  identifiedUserId = null;
  userProperties = {};
  superProperties = { ...baseSuperProperties };
  pendingEvents.length = 0;
  rotateAnonymousDistinctId();

  if (isInitialized && !isDirectHttpMode()) {
    mixpanel.reset();
    const cleanedBaseProperties = cleanAnalyticsProperties(superProperties);
    if (Object.keys(cleanedBaseProperties).length > 0) {
      mixpanel.register(cleanedBaseProperties);
    }
  }

  logAnalyticsDebug("[Mixpanel] User reset");
}

/**
 * Track a custom event.
 *
 * Use `lib/analytics/track.ts` for product instrumentation so Clarity and
 * Mixpanel receive the same event.
 */
export function trackEvent(
  name: string,
  properties?: T_AnalyticsProperties,
): boolean {
  if (!isInitialized) {
    enqueueEvent(name, properties);
    return false;
  }

  deliverEvent(name, properties);
  logAnalyticsDebug(`[Mixpanel] Event — ${name}`, properties);
  return true;
}

/** Track a page view using the shared product-event path. */
export function trackPage(
  pageName: string,
  properties?: T_AnalyticsProperties,
): boolean {
  const tracked = trackEvent(MixpanelEvents.PAGE_VIEWED, {
    page_name: pageName,
    path: pageName,
    ...properties,
  });

  logAnalyticsDebug(`[Mixpanel] Page — ${pageName}`);
  return tracked;
}

/** Set user profile properties and retain them for direct HTTP inspection. */
export function setUserProperties(properties: T_AnalyticsProperties): void {
  userProperties = {
    ...userProperties,
    ...properties,
  };

  if (!isInitialized || isDirectHttpMode()) return;

  const cleanedProperties = cleanAnalyticsProperties(properties);
  if (Object.keys(cleanedProperties).length > 0) {
    mixpanel.people.set(cleanedProperties);
  }
}

/** Register properties that should be sent with every future event. */
export function registerSuperProperties(
  properties: T_AnalyticsProperties,
): void {
  superProperties = {
    ...superProperties,
    ...properties,
  };

  if (!isInitialized || isDirectHttpMode()) return;

  const cleanedProperties = cleanAnalyticsProperties(properties);
  if (Object.keys(cleanedProperties).length > 0) {
    mixpanel.register(cleanedProperties);
  }
}
